import { Badge } from 'src/design/atoms/Badge';
import { Group } from 'src/design/atoms/Group';
import { MRT_ColumnDef } from 'mantine-react-table';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MilestoneTemplateDto } from 'src/api/FiretrolApiClient';
import { useMilestoneTemplates } from 'src/api/queries/milestoneTemplates/useMilestoneTemplates';
import { useCreateMilestoneTemplate } from 'src/api/queries/milestoneTemplates/useMilestoneTemplateMutations';
import { EditActionIcon } from 'src/design/atoms/EditActionIcon';
import { formatDate } from 'src/utils';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { BadgeShortList } from 'src/design/molecules/BadgeShortList';
import { Header } from 'src/design/organisms/Header';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { useTableExports } from 'src/design/organisms/useTableExports';

const getColumns = (): MRT_ColumnDef<MilestoneTemplateDto>[] => {
  return [
    {
      accessorKey: 'name',
      header: 'Template Name',
    },
    {
      id: 'offices',
      accessorFn: (row) => row.offices || [],
      header: 'Offices',
      Cell: ({ cell }) => {
        const offices = cell.row.original.offices ?? [];
        if (offices.length === 0) {
          return <Badge size="sm">Global</Badge>;
        }
        const officeBadges = offices.map((office) => (
          <Badge key={office.id} variant="light" size="sm">
            {office.name}
          </Badge>
        ));
        return <BadgeShortList items={officeBadges} maxItems={2} />;
      },
    },
    {
      id: 'created_by',
      accessorFn: (row) => row.createdBy?.fullName ?? 'Unknown',
      header: 'Created By',
    },
    {
      id: 'updated_at',
      accessorFn: (row) => formatDate(row.updatedAt),
      header: 'Last Updated',
    },
  ];
};

export const Milestones: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, isFetching, isError } = useMilestoneTemplates();
  const templates = data ?? [];
  const createTemplate = useCreateMilestoneTemplate();

  const handleCreate = async () => {
    const result = await createTemplate.mutateAsync();
    navigate(`/settings/milestones/templates/${result.id}`);
  };

  const columns = useMemo(() => getColumns(), []);

  const tableState = useTableState({
    identifier: 'settings-milestones',
    columns: columns,
    defaultSorting: [{ id: 'name', desc: false }],
  });

  const tableExports = useTableExports<MilestoneTemplateDto>(
    'milestone-templates',
    (data) => {
      return data.map((item) => ({
        ['Template Name']: item.name,
        ['Offices']:
          item.offices && item.offices.length > 0
            ? item.offices.map((o) => o.name).join(' | ')
            : 'Global',
        ['Created By']: item.createdBy?.fullName ?? 'Unknown',
        ['Last Updated']: formatDate(item.updatedAt),
      }));
    },
  );

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: true,
      displayColumnDefOptions: { 'mrt-row-actions': { size: 100 } },
      renderRowActions: ({ row }) => {
        const template = row.original;
        return (
          <Group>
            <EditActionIcon
              onClick={() =>
                navigate(`/settings/milestones/templates/${template.id}`)
              }
            />
          </Group>
        );
      },
      enableClickToCopy: false,
      columns: columns,
      data: templates,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      renderTopToolbarCustomActions: () => (
        <Group>
          <CreateButton onClick={handleCreate}>Create Template</CreateButton>
        </Group>
      ),
    },
    getRowId: (row) => String(row.id),
    totalCount: templates.length,
    isLoading,
    isFetching,
    isError,
    ...tableExports,
  });

  return (
    <>
      <Header
        breadcrumbProps={{
          items: [
            { text: 'Settings', href: '/settings' },
            { text: 'Milestones' },
          ],
        }}
        title="Milestones"
        helpText="Manage milestone templates for project planning and tracking."
      />

      {table}
    </>
  );
};
